import { format0, formatNumber, formatPercentScale } from 'utils/number';
import { startCase } from 'lodash-es';
import type {
  DomainKey,
  DomainProvenance,
  MilestoneKey,
  NodeKey,
  NodeKind,
  NodeSource,
  NodeUnit,
  OverrideWinner,
  ValueRecordKind,
  ProvenanceCapture,
  ResolvedValue,
  SlotInputNode,
  TearsheetDate,
  TearsheetOutputs,
  TearsheetRemedy,
  ZoneKey,
} from 'types/packages-api/slot-valuation-inputs';

/** Empty-cell indicator, matching the Master Data tables' `meta.type` default. */
export const EMPTY = '—';

/** The source kinds that name a real store, i.e. everything but `declared_only`. */
const SOURCED_KINDS = ['parquet', 'blob', 'column', 'json_column'] as const;

/**
 * Whether an empty value cell means "there is nowhere to read this from" or "there was
 * nothing there for this run" — or that we cannot yet say.
 *
 * ── WHY THREE STATES AND NOT TWO ─────────────────────────────────────────────────────
 *
 * The model map is a second request, so for a window on first paint every node's source is
 * `undefined`. Sorting an undefined source into either bucket would state a fact about
 * provenance that has not arrived — the same trap PendingValue exists to avoid for units.
 * `unknown` renders exactly as the page rendered before this function existed.
 *
 * ── AND WHY AN UNRECOGNISED SHAPE IS ALSO `unknown` ──────────────────────────────────
 *
 * A source shape that is neither the string nor a tuple whose head is a known kind is a
 * contract the frontend does not understand. Defaulting it to `sourced` would say "a store
 * exists" about something unparsed; defaulting it to `declared_only` would say "no store
 * exists", which is worse — it asserts the value can never be recorded. So it reports
 * unknown, AND {@link module:__test__.utils} asserts the live registry produces none, so a
 * fifth kind fails a test rather than quietly landing in the quiet branch.
 *
 * @param {NodeSource} [source] - The node's served `source`, undefined until the map lands.
 * @return {'declared_only' | 'sourced' | 'unknown'} What an empty cell against it means.
 */
export function valueSourceState(
  source: NodeSource | undefined
): 'declared_only' | 'sourced' | 'unknown' {
  if (source === 'declared_only') {
    return 'declared_only';
  }
  if (
    Array.isArray(source) &&
    (SOURCED_KINDS as readonly string[]).includes(source[0])
  ) {
    return 'sourced';
  }
  return 'unknown';
}

/**
 * The note shown beside an empty value cell, or null when nothing can be said.
 *
 * DELIBERATELY NOT A HIDE. An audit page that hides what it could not resolve stops being an
 * audit page — the reader cannot tell a row that was never going to have a value from one
 * that was dropped. Both stay on the page and say which they are.
 *
 * @param {('declared_only'|'sourced'|'unknown')} state - From {@link valueSourceState}.
 * @return {(string|null)} The note, or null while the source is unknown.
 */
export function emptyValueNote(
  state: ReturnType<typeof valueSourceState>
): string | null {
  switch (state) {
    case 'declared_only':
      return 'Not stored';
    case 'sourced':
      return 'Not recorded';
    default:
      return null;
  }
}

/**
 * The hover explanation for an empty value cell. Longer than the note, and it names the
 * DISTINCTION rather than restating the note.
 *
 * @param {('declared_only'|'sourced'|'unknown')} state - From {@link valueSourceState}.
 * @return {(string|undefined)} The title text, or undefined while the source is unknown.
 */
export function emptyValueTitle(
  state: ReturnType<typeof valueSourceState>
): string | undefined {
  switch (state) {
    case 'declared_only':
      return 'This node has no source to read from — it is a step in the model, explained by its formula rather than by a value.';
    case 'sourced':
      return 'This node reads from a real source, and this evaluation recorded no value in it.';
    default:
      return undefined;
  }
}

/**
 * Section label per layout zone (contract `ZoneKey`) — the tearsheet groups the
 * slot inputs by zone and titles each section from here. A zone that also has a
 * `DomainGroup` prefers the group's own `label` at the call site; these are the
 * fallback (and the only source for the zones with no domain: Type Curve, Volumes,
 * Cashflow).
 *
 * ── TWO LABELS MOVED WITH THE REVENUE LINES (INS-12209) ─────────────────────────────────
 *
 * `royalty_revenue` now reads "Royalty", because revenue left it: `revenue` and its eight
 * outputs are cashflow-zone nodes. THE KEY IS UNCHANGED — it is the contract's `ZoneKey`,
 * the join key both renderers and the payload use, so only the label moved. Key and label
 * therefore disagree here on purpose.
 *
 * `cashflow` read "Slot inputs", which described the WI / NRI / slot-data inputs the
 * cashflow formulas consume and matched the mock. It cannot survive the move: eight revenue
 * OUTPUTS landing in a block called "Slot inputs" would call a result an input, on the one
 * page whose job is telling those apart. It now reads "Cashflow", matching the registry's
 * own label for the zone.
 *
 * `volumes` no longer diverges: the registry's own label for the merged zone is now
 * "Volume Source" too. `discount_rate` reading "Discount Rates" is the one
 * remaining deliberate divergence.
 */
export const ZONE_LABELS: Record<ZoneKey, string> = {
  development_pace: 'Development Pace',
  volumes: 'Volume Source',
  ownership_interest: 'Ownership Interest',
  price_deck: 'Price Deck',
  differential: 'Differentials',
  tax: 'Taxes',
  discount_rate: 'Discount Rates',
  royalty_revenue: 'Royalty',
  expenses: 'Expenses',
  cashflow: 'Cashflow',
};

/**
 * What to call each kind of looked-up record, for the note on a value whose record is absent.
 *
 * A `Record<ValueRecordKind, string>` so a kind added to the contract is a COMPILE ERROR
 * here rather than a row saying "No  record for this id" with a hole in it.
 */
export const VALUE_RECORD_NOUNS: Record<ValueRecordKind, string> = {
  type_curve: 'type curve',
  // INS-12211. Upper-cased because it is an initialism, not a common noun: the note reads
  // "No AFE record for this id".
  afe: 'AFE',
};

/**
 * The note beside a value whose looked-up record does not exist.
 *
 * ── FLAT REGISTER, DELIBERATELY ──────────────────────────────────────────────────────
 *
 * On a pdp slot this is the CORRECT AND EXPECTED state — a pdp has no type curve, and the
 * block header already says its volumes come from the well's own production forecast. So the
 * note states a fact and nothing more: no "missing", no warning colour, no icon. What it
 * exists to prevent is the reader concluding that the link is broken, which is what an
 * unlinked id sitting beside a linked one implies.
 *
 * @param {ValueRecordKind} [kind] - The record kind the lookup missed, if any.
 * @return {(string|null)} The note, or null when there is nothing to say.
 */
export function missingRecordNote(
  kind: ValueRecordKind | null | undefined
): string | null {
  if (!kind) {
    return null;
  }
  return `No ${VALUE_RECORD_NOUNS[kind]} record for this id`;
}

/**
 * What to call the named configuration a domain's values came from.
 *
 * "Scenario" was hardcoded, which was right while only the four `*_scenarios` domains carried
 * a name. price_deck now carries one too (a deck is not a scenario), so the noun has to come
 * from the domain.
 *
 * A `Record<DomainKey, string>` ON PURPOSE: a domain added to the contract is a COMPILE ERROR
 * here rather than a row silently captioned "Scenario:" about something that is not one.
 */
export const DOMAIN_CONTEXT_NOUN: Record<DomainKey, string> = {
  development_pace: 'Scenario',
  price_deck: 'Price deck',
  differential: 'Scenario',
  tax: 'Scenario',
  discount_rate: 'Scenario',
  royalty: 'Scenario',
  expenses: 'Scenario',
};

/**
 * The order the tearsheet renders zone sections in.
 *
 * ── VOLUMES NOW PRECEDES TYPE CURVE (UAT, 9 Sep; confirmed by Sam) ──────────────────────
 *
 * It followed the mock's flow, which put Type Curve first. Andrew asked for the swap and the
 * reason is the reading order of the data: the Volumes block holds the volume SOURCE — the
 * forecast this slot was actually valued on — and the Type Curve block holds the Arps
 * parameters DERIVED FROM that source. Naming the source after showing its parameters asks
 * the reader to hold five numbers before learning what produced them.
 *
 * It also fixes the worst case rather than the average one. On a PDP slot the type curve does
 * not exist: the block states "Volumes come from this well's own production forecast, which
 * carries no type-curve parameters" (VOLUME_SOURCE_COPY). Leading with that reads as an
 * absence; leading with the volume source that IS present, and only then explaining that it
 * has no curve, reads as an explanation.
 *
 * This is a RENDER order and nothing else. ModelMap::ZONES has its own insertion order
 * (model_map.rb:287) which drives node grouping and the graph payload, not the page — the two
 * already diverge, since `expenses` sits earlier here than it does there.
 */
export const ZONE_ORDER: ZoneKey[] = [
  'development_pace',
  // ── THREE ENTRIES COLLAPSED TO ONE ────────────────────────────────────────────────────
  //
  // 'volumes', 'pdp_decline' and 'type_curve' occupied these three slots, in that order,
  // and are now one zone. The rows did not move relative to each other: the registry's
  // declaration order was re-sequenced to volumes → pdp_decline → type_curve to match what
  // this list used to produce, so the merged block reads exactly as the three blocks did
  // end to end. That is deliberate — a no-row-moved diff is what UAT can eyeball.
  //
  // The two decisions this list used to record are kept because they explain the order
  // inside the block: PDP Decline sits between Volume Source and Type Curve,
  // and Ownership Interest was moved OUT of that gap during UAT
  // UAT so the related blocks sat together. Both still hold; they are now intra-block.
  'volumes',
  'ownership_interest',
  'price_deck',
  'differential',
  'expenses',
  'tax',
  'discount_rate',
  // ROYALTY REMOVED FROM THE TEARSHEET (INS-11969). The `royalty_revenue` zone (Royalty %,
  // ORRI %, Lease Royalty %) is no longer surfaced as a domain section on the page or in the
  // export — omitting it here drops its block from both, since buildSections and
  // buildSourceBlocks iterate this order. The registry keeps the zone and its nodes
  // (royalty_percentage / orri_percentage still feed the revenue formula, and the fields are
  // load-bearing across Scenarios / ARIES / the calc blob), and the model map never drew the
  // zone anyway (slot_scoped: false). Its ZONE_LABELS entry stays — the Record must be total.
  'cashflow',
];

/**
 * Human label per node kind (the contract's five-value `NodeKind`). Shared by the
 * table view's node rows, the model-map node badges, and the diagram legend so the
 * kind vocabulary reads identically everywhere.
 *
 * ── IT SAID "SHARED BY THE TABLE VIEW'S NODE ROWS" AND WAS NOT ────────────────────────
 *
 * Nothing imported this until now; the table rendered no kind at all. That is why Sam could
 * see "Gas Shrink %" carrying 80% and "Gas Shrink & NGL Yield" carrying a formula and no
 * value, and read them as the same thing twice — the page never said one of them was a
 * derivation. See {@link derivationKindLabel}.
 */
export const NODE_KIND_LABELS: Record<NodeKind, string> = {
  input: 'Input',
  output: 'Output',
  function: 'Function',
  parameter: 'Parameter',
  scenario_config: 'Scenario config',
};

/**
 * The one-word marker for a row whose CONTENT IS ITS FORMULA, or null for every other row.
 *
 * ── WHY MARK ONE KIND AND NOT ALL FIVE ───────────────────────────────────────────────
 *
 * The other four kinds carry values, so a marker on them says nothing a reader cannot see in
 * the value cell. `function` is the kind that never carries one: its formula IS the content,
 * and that is the distinction a reader cannot make from the label alone. Marking all 176 rows
 * with a kind would add noise to the 150 that do not need it.
 *
 * ── AND WHY NOT KEY IT ON `declared_only` INSTEAD ────────────────────────────────────
 *
 * That set is all 30 unsourceable nodes and is exactly what the value cell already reads
 * "Not stored" for, so a label marker keyed on it would restate the cell beside it. `kind`
 * is a different fact: WHAT THE ROW IS, rather than whether this run recorded a value. It
 * comes straight from the registry and uses {@link NODE_KIND_LABELS}, so the word is the same
 * one the model map's legend shows — the two views agree rather than inventing vocabulary.
 *
 * @param {NodeKind} [kind] - The node's declared kind.
 * @return {(string|null)} The marker, or null when the row needs none.
 */
export function derivationKindLabel(kind: NodeKind | undefined): string | null {
  return kind === 'function' ? NODE_KIND_LABELS.function : null;
}

/** The kinds in a stable legend / rendering order. */
export const NODE_KINDS: NodeKind[] = [
  'scenario_config',
  'parameter',
  'function',
  'input',
  'output',
];

/**
 * Fill / stroke / text colour per node kind — the Lexicon-aligned palette from the
 * model-map mock, keyed to the backend's five kinds. Used by the GoJS node
 * template, the legend swatches, and the table view's kind tags so a kind's colour
 * is single-sourced. An unrecognised kind falls back to a neutral grey at the call
 * site.
 */
export const NODE_KIND_COLORS: Record<
  NodeKind,
  { fill: string; stroke: string; text: string }
> = {
  scenario_config: { fill: '#e8daff', stroke: '#8a3ffc', text: '#31135e' },
  parameter: { fill: '#9ef0f0', stroke: '#007d79', text: '#004144' },
  function: { fill: '#d9fbfb', stroke: '#009d9a', text: '#004144' },
  input: { fill: '#defbe6', stroke: '#24a148', text: '#044317' },
  output: { fill: '#ffd6e8', stroke: '#9f1853', text: '#510224' },
};

/** Neutral fallback for a kind not in {@link NODE_KIND_COLORS} (kinds are an open set server-side). */
export const NODE_KIND_FALLBACK_COLOR = {
  fill: '#e0e0e0',
  stroke: '#8d8d8d',
  text: '#161616',
};

/**
 * Unit suffix for a series column header, or null when the unit asserts nothing renderable.
 *
 * TOTAL OVER NodeUnit ON PURPOSE. A `Record<NodeUnit, ...>` means a new unit added to the
 * contract is a COMPILE ERROR here rather than a column that silently loses its unit — which
 * is the failure this map exists to prevent.
 *
 * `unknown` maps to null deliberately: nobody has established that node's unit, and printing
 * a guess in a header is worse than printing none. `dimensionless` and `none` are also null,
 * but for the opposite reason — there genuinely is no unit to state.
 */
export const UNIT_HEADER_SUFFIX: Record<NodeUnit, string | null> = {
  usd: 'USD',
  usd_per_bbl: 'USD/BBL',
  usd_per_mcf: 'USD/MCF',
  bbl: 'BBL',
  mcf: 'MCF',
  bbl_per_month: 'BBL/mo',
  mcf_per_month: 'MCF/mo',
  // The ramp-up slope `n`: the monthly rate's climb per month (INS-13307). Formatted as its
  // rate is, by formatNodeValue's plain numeric branch; only the header says "per month²".
  bbl_per_month_per_month: 'BBL/mo²',
  mcf_per_month_per_month: 'MCF/mo²',
  bbl_per_mmcf: 'BBL/MMCF',
  // SHOWN PER MMcf: formatNodeValue multiplies a per-Mcf value by unitDisplayScale.
  bbl_per_mcf: 'BBL/MMCF',
  feet: 'ft',
  months: 'months',
  // NULL, AND NOT BECAUSE THESE HAVE NO UNIT. formatNodeValue renders the percent family
  // INLINE on every cell — `fraction` is scaled x100 and suffixed, `percent` and
  // `percent_per_year` are suffixed as-is (formatNodeValue, below). A header suffix as well would
  // state the unit twice in every row. The cell owns it; the header stays quiet.
  percent: null,
  percent_per_year: null,
  fraction: null,
  date: null,
  dimensionless: null,
  none: null,
  unknown: null,
};

/**
 * The factor a unit's stored number is multiplied by for display (INS-13307).
 *
 * One unit only: `bbl_per_mcf`, the water/gas ratio as the calculator stores and applies it.
 * The Type Well Profile Config form takes bbl/MMcf, so the page shows ×1,000 under BBL/MMCF
 * ({@link UNIT_HEADER_SUFFIX}). Display only: the stored column, the warehouse and the ARIES
 * WOR rows keep the per-Mcf number.
 *
 * @param {NodeUnit} [unit] - The node's served unit.
 * @return {number} The display factor, 1 for every other unit.
 */
export function unitDisplayScale(unit?: NodeUnit): number {
  return unit === 'bbl_per_mcf' ? 1000 : 1;
}

/**
 * Formats one resolved node value for display. A number keeps its natural
 * precision (grouped thousands, up to 8 decimals, trailing zeros trimmed) — so an
 * NRI reads `0.01415025` and a b-factor `1.12`, not a rounded `0`. Booleans read
 * Yes/No; an ISO date string (`2009-07-24`) renders `MM/DD/YYYY`; any other string
 * passes through; null / undefined render as the empty indicator.
 *
 * ── THE UNIT IS NOW APPLIED, AND THE OLD COMMENT HERE WAS TRUE WHEN IT WAS WRITTEN ────
 *
 * This used to say "NO UNIT is applied — the payload carries none (contract note 2)". That
 * held at the merge-base; a later change then declared a unit on all 221 nodes, so
 * the ratio nodes were rendering raw — `oil_tax_percentage` read "0.046" where it means
 * 4.6%.
 *
 * SCALING COMES FROM THE DECLARED UNIT, NEVER FROM THE MAGNITUDE. `percent` (0-100, 24
 * nodes) and `fraction` (0-1, 7) are indistinguishable in the payload, so guessing from the
 * value is how a 4.6% severance tax renders as 0.046% or 460%:
 *
 *   fraction           x100, then '%'  — 0.046 -> "4.6%"
 *   percent            '%' as-is, NO scaling — 80 -> "80%"
 *   percent_per_year   '%/yr' as-is, no scaling. Same family, same 100x risk; the period is
 *                      part of the unit and the calculator converts it before use.
 *   unknown            the bare number, no unit, NO GUESS. Reported distinctly — see
 *                      {@link isUnitUnestablished} — because `unknown` and `dimensionless`
 *                      are DIFFERENT FACTS: one asserts there is no unit, the other that
 *                      nobody has established it, and a wrong unit is worse than no unit on
 *                      a page whose purpose is explaining where numbers came from.
 *   everything else    unchanged from before this commit — a bare grouped number, a date, a
 *                      Yes/No. Deliberately NOT extended to usd / bbl / mcf / feet / months
 *                      in this commit: those render bare today and a symbol or suffix there
 *                      is a design decision, not a correctness fix.
 *
 * A missing unit (the model map has not loaded yet) formats exactly as it did before, so the
 * table still paints from the tearsheet response alone.
 *
 * `scaledMaxPrecision` overrides the decimal cap on the SCALED units (fraction / percent /
 * percent_per_year) only; the page leaves it undefined and keeps its 4-decimal default, while
 * the Excel export passes 8 so an NRI like 0.00390625 lands as 0.390625% rather than a rounded
 * 0.3906% — the default numeric branch already keeps 8 decimals, and the export must match it.
 *
 * FOUR DECIMALS ON SCREEN IS THE DELIBERATE PRODUCT ANSWER FOR OWNERSHIP INTERESTS (INS-11969
 * review, confirmed with Product). NRI / NWI (both `:fraction`) render 0.3906% on the tearsheet
 * by this default — NOT the export's 0.390625% — on purpose: the page is a scannable per-slot
 * view where a fifth/sixth decimal adds density without changing the reading, and a reader who
 * must reconcile the exact figure the calculator consumed goes to the workbook, which carries
 * full precision. This is a decision for interests specifically, not the generic default merely
 * falling through.
 *
 * @param {(number|string|boolean|null)} value - The scalar value to format.
 * @param {NodeUnit} [unit] - The node's declared unit, when the model map is loaded.
 * @param {number} [scaledMaxPrecision] - Decimal cap for scaled units; defaults to 4.
 * @return {string} The display string.
 */
export function formatNodeValue(
  value: ResolvedValue['value'],
  unit?: NodeUnit,
  scaledMaxPrecision?: number
): string {
  if (value === null || value === undefined) {
    return EMPTY;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return EMPTY;
    }
    switch (unit) {
      case 'fraction':
        return formatPercentScale(value * 100, EMPTY, scaledMaxPrecision);
      case 'percent':
        return formatPercentScale(value, EMPTY, scaledMaxPrecision);
      case 'percent_per_year':
        return `${formatPercentScale(value, EMPTY, scaledMaxPrecision)}/yr`;
      case 'bbl_per_mcf':
        return formatNumber(
          value * unitDisplayScale(unit),
          0,
          8,
          'decimal',
          true
        );
      default:
        // Grouped thousands, 0–8 decimals (trailing zeros trimmed by max precision).
        return formatNumber(value, 0, 8, 'decimal', true);
    }
  }
  // ISO date (yyyy-mm-dd, optionally with a time) → MM/DD/YYYY, matching the mock.
  return formatIsoDate(value) ?? value;
}

/**
 * Nodes whose ON-SCREEN value is shown at a fixed number of decimals, overriding
 * {@link formatNodeValue}'s default 0–8 (INS-12206).
 *
 * DISPLAY ONLY, AND DELIBERATELY TINY. Andrew asked for the formation subsea elevation
 * (`formation_depth`, an 8-decimal feet value) at 2 dp — "we definitely don't need 8 decimal
 * places of depth". No other row was asked for and other rows may legitimately need their
 * precision, so a node is added here ONLY on the same explicit ask — never a blanket rule by
 * unit. The Excel export and everything that feeds a calculation keep full precision.
 */
export const DISPLAY_DECIMALS: Partial<Record<NodeKey, number>> = {
  formation_depth: 2,
};

/**
 * Nodes rounded to a fixed number of decimals on the page AND in the Excel export (INS-13307).
 *
 * Unlike {@link DISPLAY_DECIMALS}, the rounding travels into the workbook, because that is
 * what was asked: Misha's UAT wanted Slot Length as a whole number of feet on both surfaces
 * (13,936.50758174 → 13,937). DISPLAY ONLY all the same — the stored value, the volume
 * adjustment ratio and pricing all keep the full-precision length. Same rule as above: a
 * node is added here only on an explicit ask.
 *
 * Type Curve Lateral Length was checked and left out: every type curve header carries a
 * whole number of feet, so it already renders as one.
 */
export const ROUNDED_DECIMALS: Partial<Record<NodeKey, number>> = {
  slot_length: 0,
  // Sandbox review (INS-13307): a ramp-up's starting rate and slope at 2 decimals, the priced
  // version's (tc_*) and a DCA forecast's (pdp_*) alike, as their rows read the same
  // (10,168.27135 → 10,168.27). The run still prices the stored value.
  ...Object.fromEntries(
    ['tc', 'pdp'].flatMap((prefix) =>
      ['oil', 'gas', 'water'].flatMap((stream) => [
        [`${prefix}_${stream}_ramp_up_qi`, 2],
        [`${prefix}_${stream}_ramp_up_slope`, 2],
      ])
    )
  ),
};

/**
 * Nodes whose value is an IDENTIFIER, shown as the plain integer on the page and in the Excel
 * export, with no thousands separator (INS-13307 sandbox review: Type Curve Version 3282, not
 * 3,282; a PDP's Volume Source 824736, not 824,736). A grouped id reads as a quantity and does not
 * match the id anywhere else it appears. The id-valued nodes in the registry: the priced version's
 * type_curve_header_details id, the volume source (a DCA forecast's id on a PDP slot) and the AFE's
 * afe_headers id. Slot number is an ordinal and every other number is a quantity, so none is here.
 */
export const PLAIN_ID_NODES: ReadonlySet<NodeKey> = new Set<NodeKey>([
  'tc_type_curve_version',
  'slot_volume_source',
  'afe_source_id',
]);

/**
 * The plain integer for a {@link PLAIN_ID_NODES} node holding a whole number, or null to fall
 * through to the node's other formatting.
 *
 * @param {NodeKey} key - The node's key.
 * @param {(number|string|boolean|null)} value - The scalar value.
 * @return {(string|null)} "3282", or null.
 */
function formatPlainId(
  key: NodeKey,
  value: ResolvedValue['value']
): string | null {
  return PLAIN_ID_NODES.has(key) &&
    typeof value === 'number' &&
    Number.isInteger(value)
    ? String(value)
    : null;
}

/**
 * A finite, plainly-formatted number at a fixed number of decimals, or null where no fixed
 * count applies — no decimals given, not a finite number, or a SCALED unit
 * (percent / fraction / percent_per_year), whose own branch owns its precision.
 *
 * @param {(number|string|boolean|null)} value - The scalar value to format.
 * @param {NodeUnit} [unit] - The node's served unit.
 * @param {number} [decimals] - The fixed decimal count, when the node declares one.
 * @return {(string|null)} The rounded string, or null to fall through to formatNodeValue.
 */
function formatFixedDecimals(
  value: ResolvedValue['value'],
  unit: NodeUnit | undefined,
  decimals: number | undefined
): string | null {
  if (
    decimals === undefined ||
    typeof value !== 'number' ||
    !Number.isFinite(value) ||
    unit === 'fraction' ||
    unit === 'percent' ||
    unit === 'percent_per_year'
  ) {
    return null;
  }
  return formatNumber(value, decimals, decimals, 'decimal', true);
}

/**
 * The value string for a node ON THE PAGE — {@link formatNodeValue}, but honouring a per-node
 * fixed-decimal override ({@link DISPLAY_DECIMALS}, then {@link ROUNDED_DECIMALS}) for the
 * few rows that declare one.
 *
 * ── THE EXPORT MUST NOT CALL THIS ─────────────────────────────────────────────────────────
 *
 * A spreadsheet is where a reader goes FOR precision, and a rounded value written into a cell
 * can propagate silently into their own maths (INS-12206 AC). So `exportValue` calls
 * {@link formatNodeExport}, which skips the page-only {@link DISPLAY_DECIMALS}.
 *
 * The override applies only to a finite number, and never to a SCALED unit
 * (percent / fraction / percent_per_year), whose own branch owns its precision — the map is
 * for plainly-formatted numbers like feet. Anything else falls through unchanged.
 *
 * @param {NodeKey} key - The node's key, to look up any override.
 * @param {(number|string|boolean|null)} value - The scalar value to format.
 * @param {NodeUnit} [unit] - The node's served unit.
 * @return {string} The display string.
 */
export function formatNodeDisplay(
  key: NodeKey,
  value: ResolvedValue['value'],
  unit?: NodeUnit
): string {
  return (
    formatPlainId(key, value) ??
    formatFixedDecimals(
      value,
      unit,
      DISPLAY_DECIMALS[key] ?? ROUNDED_DECIMALS[key]
    ) ??
    formatNodeValue(value, unit)
  );
}

/**
 * The value string for a node IN THE EXCEL EXPORT — {@link formatNodeValue} at the export's
 * precision, but honouring {@link ROUNDED_DECIMALS}, the few rows rounded on both surfaces.
 * {@link DISPLAY_DECIMALS} stays page-only and is deliberately not consulted here.
 *
 * @param {NodeKey} key - The node's key, to look up any override.
 * @param {(number|string|boolean|null)} value - The scalar value to format.
 * @param {NodeUnit} [unit] - The node's served unit.
 * @param {number} [scaledMaxPrecision] - Decimal cap for scaled units.
 * @return {string} The export string.
 */
export function formatNodeExport(
  key: NodeKey,
  value: ResolvedValue['value'],
  unit?: NodeUnit,
  scaledMaxPrecision?: number
): string {
  return (
    formatPlainId(key, value) ??
    formatFixedDecimals(value, unit, ROUNDED_DECIMALS[key]) ??
    formatNodeValue(value, unit, scaledMaxPrecision)
  );
}

/**
 * Whether a node's unit is UNESTABLISHED as opposed to absent.
 *
 * `unknown` and `dimensionless` both render as a bare number, so without this they would be
 * visually identical while meaning opposite things — "nobody has established this" against
 * "this genuinely has no unit". The caller marks the former so a reader is not led to infer
 * a unit that was never determined. Two nodes are `unknown` today (pace_model_value, q_lim);
 * eight are `dimensionless`.
 *
 * @param {NodeUnit} [unit] - The node's declared unit.
 * @return {boolean} True only for an explicitly unestablished unit.
 */
export function isUnitUnestablished(unit?: NodeUnit): boolean {
  return unit === 'unknown';
}

/**
 * Formats one entry of `outputs.values`.
 *
 * ── THE UNION IS NOT number ANY MORE, AND THE OLD GUARD DROPPED DATA SILENTLY ────────
 *
 * This took `value: number` and guarded with `Number.isFinite`. INS-12069 widened
 * `TearsheetOutputs.values` to `Record<string, number | string | null>` when `irr` and
 * `payout_month` were appended to slot_pricing, and `payout_month` arrives as an ISO date
 * STRING. `Number.isFinite('2024-03-01')` is false — no coercion — so a real payout date
 * fell through to the empty marker. No crash, no error, the value simply gone.
 *
 * It would also have shipped completely unexercised: every slot reachable locally has
 * `net_working_interest` 0.0, so payout requires an investment that was never made and the
 * column is null on all of them. A green suite proving nothing — the INS-11996 shape.
 *
 * Each arm of the union is handled explicitly rather than defaulted:
 *
 *   number   the six NPVs — grouped, no decimals, no currency symbol
 *   string   an ISO date (`payout_month`) — rendered MM/DD/YYYY, matching
 *            {@link formatNodeValue}'s date arm so one date reads identically in both
 *            halves of the payload. A non-date string passes through verbatim.
 *   null     the empty marker. `irr` null means NO IRR EXISTS and `payout_month` null means
 *            there was no investment to recover — neither is a zero, and neither may render
 *            as one.
 *
 * @param {(number|string|null)} value - One `outputs.values` entry.
 * @param {NodeUnit} [unit] - The output key's declared unit, when the model map is loaded.
 * @param {number} [scaledMaxPrecision] - Max decimals for a scaled (fraction/percent) value;
 *   the export passes EXPORT_SCALED_MAX_PRECISION, the page leaves it undefined (4-dp default).
 * @return {string} The display string.
 */
export function formatOutputValue(
  value: number | string | null | undefined,
  unit?: NodeUnit,
  scaledMaxPrecision?: number
): string {
  if (value === null || value === undefined) {
    return EMPTY;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return EMPTY;
    }
    // THE OUTPUT KEYS ARE REGISTRY NODES, so they carry units like any other — and the
    // six NPVs are the only ones `format0` suits. `irr` is declared `fraction` and is a RAW
    // xirr rate: an 18.52% return arrives as 0.1852, which format0 rounds to "0". Found
    // while proving the payout_month arm on a synthetic value; local data cannot produce a
    // non-null irr either, so it would have shipped rendering every real IRR as zero.
    //
    // `scaledMaxPrecision` is the export's lever (EXPORT_SCALED_MAX_PRECISION): the page caps
    // a fraction at 4 dp, but the workbook is where a reader goes FOR precision, so an output
    // `irr` must not be rounded there while an NRI input two blocks up is not. Undefined on
    // the page path, which keeps the 4-dp default.
    return unit === 'fraction' || unit === 'percent'
      ? formatNodeValue(value, unit, scaledMaxPrecision)
      : format0(value);
  }
  return formatIsoDate(value) ?? value;
}

/**
 * `2024-03-01` → `03/01/2024`, or null when the string is not an ISO date. Extracted so the
 * node values and the output values cannot drift into two date formats.
 *
 * @param {string} value - A candidate ISO date string.
 * @return {(string|null)} The formatted date, or null when it is not one.
 */
function formatIsoDate(value: string): string | null {
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return iso ? `${iso[2]}/${iso[3]}/${iso[1]}` : null;
}

/**
 * The display label for an output row.
 *
 * ── THE REGISTRY ALREADY HAS THESE RIGHT AND THE FRONTEND WAS RE-DERIVING THEM WORSE ──
 *
 * This used to be `startCase(key)` alone, which mangles every initialism in the set —
 * ALL EIGHT output rows were wrong:
 *
 *   target_npv_excluded   "Target Npv Excluded"  ->  "Target NPV (excluded)"
 *   mtm_npv_included      "Mtm Npv Included"     ->  "MTM NPV (included)"
 *   irr                   "Irr"                  ->  "IRR"
 *
 * The registry has carried the correct label all along (`model_map.rb`); the Calculated
 * result was the one section that ignored it, so `outputLabel` humanised the KEY instead.
 * A reader scanning a valuation tearsheet for "IRR" would not find "Irr".
 *
 * `startCase` remains the fallback for a key the model map has not described — the map is
 * a second request, so a label must exist before it lands, and an unmangled row is better
 * than an empty one.
 *
 * @param {string} key - The output key.
 * @param {string} [registryLabel] - The served `ModelMapNode.label`, when the map is loaded.
 * @return {string} The label to display.
 */
export function outputLabel(key: string, registryLabel?: string): string {
  return registryLabel && registryLabel.length > 0
    ? registryLabel
    : startCase(key);
}

/**
 * Whether a PDP Decline row has nothing behind it on this slot, and so does not render at
 * all.
 *
 * ── REMOVED, NOT FADED, AND ONLY HERE ────────────────────────────────────────────────────
 *
 * The page's general rule is that an unresolved row STAYS and says which kind of empty it is
 * — hiding it would leave a reader unable to tell an unsourceable node from a dropped value.
 * This zone is the one exception: a forecast is fitted per stream, so a slot
 * with water only would otherwise show eight permanently blank rows beside its four real
 * ones. The block caption explains the absence instead, which is what the rows would have.
 *
 * DERIVED FROM THE REGISTRY, NOT A KEY LIST. The registry declares a per-stream node's source
 * as `[:blob, :dca_parameters, 'water.qi']` and a section-level one as
 * `[:blob, :dca_parameters, 'di_format']` — the dot IS the distinction, so a stream or a
 * parameter added later inherits this with no edit here. Everything in source-blocks.ts
 * refuses to be a key list for the same reason (see WITHDRAWN_ROWS, the one exception, and
 * why it had to be declared).
 *
 * ⚠️ WIDENED FROM PER-STREAM ROWS TO EVERY ROW IN THE ZONE. The three section-level rows
 * used to render regardless, on the reasoning that their absence was informative. Measured on
 * sandbox: 405 of 424 non-paceable slots are Legacy and have no parameters at all, so those
 * three printed "Not recorded" on 96% of the wells and the section carried no
 * information whatsoever, which made the section useless.
 * The block's decline NAME and its caption now carry the meaning instead, and a row
 * with nothing behind it is simply absent.
 *
 * The dot in the source path no longer matters, which is why this reads the vocabulary alone
 * — it is still derived from the registry, and a row added to the zone later inherits it.
 *
 * @param {NodeKey} key - The node.
 * @param {NodeSource} [source] - The node's served `source`.
 * @param {boolean} applied - Whether the node resolved a value.
 * @return {boolean} True when the row is a parameter for an absent stream.
 */
export function isUnfittedDeclineRow(
  key: NodeKey,
  source: NodeSource | undefined,
  applied: boolean
): boolean {
  if (applied) {
    return false;
  }
  // Matching on the VOCABULARY at source[1] keeps this one predicate — a parameter added to any
  // of the stores inherits it with no edit here, which is why this is not a key list.
  //
  // PLUS THE PRICED VERSION'S ID, the one row of the priced type curve that is not in its
  // vocabulary — see TYPE_CURVE_VERSION_KEY.
  return isDeclineParameterSource(source) || key === TYPE_CURVE_VERSION_KEY;
}

/**
 * ── THE PRICED TYPE CURVE'S VERSION, READ FROM THE SLOTS BLOB (INS-13307) ────────────────
 *
 * `tc_type_curve_version` heads the priced type curve's rows, and is the one of them NOT read
 * from `type_curve_dca_parameters`: the run stamps the version id on the slot's own row of
 * the slots blob. Its `slots` vocabulary is every slot fact — category, depth, AFE — and an
 * unresolved one of those keeps its faded row, so the vocabulary cannot be the test here; the
 * node is (not a copy of model_map.rb's source tuple).
 *
 * Without it a PDP or Legacy slot would show a lone blank "Type Curve Version" row among its
 * decline parameters — the absent-source row every other part of this rule removes.
 */
const TYPE_CURVE_VERSION_KEY: NodeKey = 'tc_type_curve_version';

/**
 * Whether a node's served source is one of the decline-parameter stores — the test
 * {@link isUnfittedDeclineRow} applies, exposed so the water gates (source-blocks.ts) select
 * the same rows by the same rule rather than by a list of keys.
 *
 * @param {NodeSource} [source] - The node's served `source`.
 * @return {boolean} True for a `dca_parameters` or `type_curve_dca_parameters` blob source.
 */
export function isDeclineParameterSource(
  source: NodeSource | undefined
): boolean {
  return (
    Array.isArray(source) &&
    source[0] === 'blob' &&
    DECLINE_PARAMETER_VOCABULARIES.has(source[1])
  );
}

/**
 * The TWO stores a decline parameter can be read out of, both blob columns, both the new DCA schema
 * (INS-13307): `dca_parameters` (a slot valued from its DCA forecast) and
 * `type_curve_dca_parameters` (the type curve version a run priced).
 *
 * A store this slot does not have is removed rather than shown as blank rows: a PDP slot loses
 * all 29 `tc_*` rows.
 *
 * A slot carries AT MOST ONE of the two, and that is what makes the rule safe to apply
 * across both. The DCA builder gates on `forecast_source`; the type-curve
 * read is keyed on the type-curve row's `slot_volume_source`, the curve the run priced and
 * recorded (BlobTypeCurveVersions.curve_id), which a PDP row does not have. Not on
 * `unique_tc_id`: that is the formation's curve, and it differs from the priced one on some
 * rows (INS-13307 audit B3). One scalar, one source.
 *
 * ── WHY THE TYPE-CURVE STORE JOINED THIS SET (the volume-source merge) ───────────────────
 *
 * Type Curve used to be its own block, so a PDP slot never saw its rows — the whole block
 * was replaced by a caption. Merged into one block there is no block left to replace, and
 * without this a PDP slot would render permanently blank type-curve rows beside its real ones. That is the failure already ruled out once for this zone,
 * arriving from the other direction.
 *
 * THE RULE IS THE SAME FOR BOTH: a parameter row for a source this slot does not have, or a
 * stream its forecast does not carry, is removed rather than faded.
 */
const DECLINE_PARAMETER_VOCABULARIES: ReadonlySet<string> = new Set([
  'dca_parameters',
  'type_curve_dca_parameters',
]);

/**
 * Whether a node is one of the per-category base discount rates that did NOT apply to this
 * slot, and so renders de-emphasised per AC 4.
 *
 * ── THE CATEGORY IS SERVED DATA NOW; IT USED TO BE A REGEX ON THE KEY ────────────────
 *
 * The previous implementation matched `/^(?:target|max|mtm)_(pdp|ltd|duc|permit)_.../` and
 * carried a comment claiming "adding a fifth category needs no edit here". That was FALSE:
 * both halves were closed enumerations, so a fifth category's rate node would not match,
 * would report no category, and would render EMPHASISED on every slot — the AC 4 defect,
 * reintroduced under a comment asserting it was handled. The registry now declares
 * `category` on those nodes and serves it beside `unit`, so a new category is a registry
 * edit with no change here at all.
 *
 * DISTINCT FROM THE UNRESOLVED FADE. A node with no value is faded because there is nothing
 * to show; these all HAVE values and are all `applied` — the rest are the option group that
 * lost. Conflating the two would tell the user a rate is missing when it is present and
 * merely inapplicable.
 *
 * FAILS OPEN, DELIBERATELY. No node category (the model map has not loaded, or the node is
 * not a categorised one) and no slot category both mean "do not fade". Showing a rate that
 * should be faded is a smaller lie than fading one that applied.
 *
 * @param {string} [nodeCategory] - The node's served reserve category.
 * @param {(string|null)} slotCategory - The slot's `slot_category` value.
 * @return {boolean} True when this rate belongs to another category.
 */
export function isUnappliedCategoryRate(
  nodeCategory: string | undefined,
  slotCategory: string | null
): boolean {
  if (!nodeCategory || !slotCategory) {
    return false;
  }
  return nodeCategory !== slotCategory;
}

/**
 * The slot's reserve category, read from the `slot_category` NODE.
 *
 * NOT from `header.slot` — that carries wellName, slotNumber, formationName, basin, tier,
 * unit and unitTractId, and no category (verified against a live payload). The category is a
 * registry node like any other input, and reading it from the node keeps one source.
 *
 * @param {SlotInputNode[]} nodes - The resolved slot inputs.
 * @return {(string|null)} The category, or null when it did not resolve.
 */
export function slotCategoryOf(nodes: SlotInputNode[]): string | null {
  const node = nodes.find((candidate) => candidate.key === 'slot_category');
  const value = node?.applied ? node.values[0]?.value : null;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * The four schedule milestones, in lifecycle order, with their display labels. Keyed by the
 * contract's `MilestoneKey` — the same four `response.dates` carries.
 */
export const MILESTONE_ORDER = [
  'permitDate',
  'spudDate',
  'modeledCompletionDate',
  'firstProductionDate',
] as const;

export const MILESTONE_LABELS: Record<
  (typeof MILESTONE_ORDER)[number],
  string
> = {
  permitDate: 'Permit Date',
  spudDate: 'Spud Date',
  modeledCompletionDate: 'Completion Date (in use)',
  firstProductionDate: 'First Production Date',
};

/**
 * The human label for a date's provenance. ALL FOUR SOURCES ARE HANDLED EXPLICITLY and an
 * unrecognised one is reported as unknown rather than defaulted.
 *
 * A default-to-`scheduled` branch would put a modelled formula next to a real vendor date,
 * which is the exact failure AC 10 exists to prevent — so an unfamiliar source must fall
 * through to "no formula" (see {@link showsScheduleFormula}), not to the modelled one.
 * The vocabulary is the schedule read's own (spec 9.2) and matches the dialog's:
 * dps-capture.ts.
 *
 * ── THE FOURTH ONE SAYS WHERE THE VALUE CAME FROM, NOT WHAT IT MEANS ────────────────
 *
 * `derivedFromFirstProduction` is a completion date the blob does not carry: the merger
 * substitutes first_production_date (scheduled_date_merger.rb#backfill_legacy_completion_date).
 * It read `Scheduled here` on 30 of 45 valued slots — a claim that the scheduler placed a
 * date it never persisted. The value is unchanged; only the claim about it is.
 *
 * @param {(string|null)} source - The date's `source` from `response.dates`.
 * @return {string} The display label.
 */
export function dateSourceLabel(source: string | null | undefined): string {
  switch (source) {
    case 'onFile':
      return 'On file';
    case 'scheduled':
      return 'Scheduled here';
    case 'override':
      return 'Set by you';
    case 'derivedFromFirstProduction':
      return 'Derived from first production';
    case null:
    case undefined:
      return EMPTY;
    default:
      return 'Unknown source';
  }
}

/**
 * Whether a date's row shows the schedule formula that produced it (AC 10).
 *
 * ── THIS IS THE WHOLE POINT, AND IT IS PER DATE ─────────────────────────────────────
 *
 * An `onFile` date came from the well's own record, not from a calculation, so showing a
 * schedule formula beside it claims the number was derived when it was reported. A
 * `scheduled` date WAS derived and its formula is the explanation. An `override` was typed by
 * a user, so no formula applies to it either.
 *
 * ONLY `scheduled` shows one. Everything else — including an unrecognised source — shows
 * none, because the failure mode is asymmetric: omitting a formula that applied loses an
 * explanation, while showing one that did not applies a fiction to a real vendor date.
 *
 * This cannot be decided per ZONE. On EC 60's DUC slot 4249535088 the permit and spud dates
 * are `onFile` while completion and first production are `scheduled` — all four in the
 * development_pace zone. A zone-level predicate is right on the PDP slots and wrong on that
 * one.
 *
 * @param {(string|null)} source - The date's `source`.
 * @return {boolean} True only for a scheduled date.
 */
export function showsScheduleFormula(
  source: string | null | undefined
): boolean {
  return source === 'scheduled';
}

/**
 * The schedule formula for a slot's category — the `duc` schedule function's formula on a
 * duc slot.
 *
 * ── ALSO SERVED DATA NOW, FOR THE SAME REASON ────────────────────────────────────────
 *
 * This built the key by hand (`"${category}_schedule"`). That is the same technique the
 * discount-rate regex used and it failed the same way, only more gently: a renamed or
 * missing node yields no formula rather than a wrong emphasis, but it is equally silent.
 * Having one lookup as data and one as a naming convention would mean the next reader has to
 * know which is which, so both read the same served `category`.
 *
 * The four schedule nodes are `kind: 'function'`; the twelve rate nodes carrying the same
 * category are `scenario_config`. Matching on kind is what separates them — no key
 * construction, no zone assumption.
 *
 * @param {(string|null)} category - The slot's `slot_category`.
 * @param {SlotInputNode[]} nodes - The resolved slot inputs.
 * @param {object} meta - The served node definitions, keyed by node.
 * @return {(string|null)} The formula, or null when there is none for this category.
 */
export function scheduleFormulaFor(
  category: string | null,
  nodes: SlotInputNode[],
  meta: Partial<Record<string, { category?: string }>>
): string | null {
  return scheduleNodeFor(category, nodes, meta)?.formula ?? null;
}

/**
 * The schedule FUNCTION NODE for a slot's category — what scheduleFormulaFor reads its
 * formula off.
 *
 * Split out because UAT 5 needs the node's declared `inputs`, not only its formula text, and
 * two lookups that must agree about which node is "the schedule for this category" is one
 * lookup too many.
 *
 * @param {(string|null)} category - The slot's `slot_category`.
 * @param {SlotInputNode[]} nodes - The resolved slot inputs.
 * @param {object} meta - The served node definitions, keyed by node.
 * @return {(SlotInputNode|undefined)} The schedule function node, if this category has one.
 */
export function scheduleNodeFor(
  category: string | null,
  nodes: SlotInputNode[],
  meta: Partial<Record<string, { category?: string }>>
): SlotInputNode | undefined {
  if (!category) {
    return undefined;
  }
  // Each category, the Create New Well statuses included, reads its OWN schedule node: they are
  // paced on their own settings (INS-13302).
  return nodes.find(
    (candidate) =>
      candidate.kind === 'function' &&
      meta[candidate.key]?.category === category
  );
}

/** One operand of a schedule formula, with whatever the run resolved it to. */
export interface ScheduleOperand {
  key: string;
  label: string;
  /** Formatted for display. {@link EMPTY} when the run resolved nothing. */
  text: string;
  /** False when this run resolved no value at all — distinct from a value of zero. */
  resolved: boolean;
}

/**
 * ── THE OPERANDS OF A SCHEDULE FORMULA, WITH THEIR VALUES (UAT 2, 9 Sep) ─────────────────
 *
 * Andrew: do not only print "reference date + permit-to-spud lag months" — print the
 * reference date and the lag, so the reader can see how the run reached 1 April 2027.
 *
 * ── READ FROM `inputs`, NOT FROM THE FORMULA TEXT ────────────────────────────────────────
 *
 * The obvious implementation tokenises the formula string and looks up each identifier. It is
 * not needed and it must not start here: the schedule node already DECLARES every operand in
 * its `inputs`, and `duc_schedule`'s five inputs are exactly the five names its formula
 * mentions. Parsing a declared formula is the technique this codebase refuses elsewhere for
 * the same reason — `slot_volume_source` is served verbatim precisely because "deriving
 * either would be inventing a schema".
 *
 * ── ABSENT IS NOT ZERO, AND THIS IS WHERE THAT COSTS SOMETHING ───────────────────────────
 *
 * A lag of 0 is a real value and a common one — `duc_completion_to_first_production_lag_months`
 * is 0 on the measured DUC. An operand the run never resolved is a different fact. So
 * `resolved` keys off whether a ResolvedValue ENTRY EXISTS, never off the value's truthiness:
 * `values[0]?.value || fallback` would turn that real 0 into an absence, which is the exact
 * confusion this epic keeps removing. An unresolved operand renders EMPTY and is marked.
 *
 * ── NARROWED TO ONE MILESTONE (UAT 1) ────────────────────────────────────────────────────
 *
 * This took the schedule NODE and walked its whole `inputs` list, so every date row showed
 * every operand of all four milestones — the same defect as the shared formula string, one
 * level down. It now takes the key list for ONE milestone, served per date as
 * `formulaInputs` from ModelMap::SCHEDULE_MILESTONE_FORMULAS. Still no parsing: the caller
 * hands over a declared list.
 *
 * @param {(string[]|undefined)} operandKeys - This milestone's own operands, in registry order.
 * @param {SlotInputNode[]} nodes - Every resolved slot input, to look operands up in.
 * @param {object} meta - Served node definitions, for each operand's label and unit.
 * @return {ScheduleOperand[]} One per key, in the order given. Empty when there are none.
 */
export function scheduleOperands(
  operandKeys: string[] | undefined,
  nodes: SlotInputNode[],
  meta: Partial<Record<string, { label?: string; unit?: NodeUnit }>>
): ScheduleOperand[] {
  if (!operandKeys?.length) {
    return [];
  }
  const byKey = new Map(nodes.map((node) => [node.key, node]));
  return operandKeys.map((key) => {
    const operand = byKey.get(key);
    // LENGTH, not truthiness — a resolved 0 must not read as absent.
    const entry = operand?.values?.length ? operand.values[0] : undefined;
    return {
      key,
      label: meta[key]?.label ?? operand?.label ?? key,
      text: entry ? formatNodeValue(entry.value, meta[key]?.unit) : EMPTY,
      resolved: Boolean(entry),
    };
  });
}

/** One operand of a substituted formula: its value formatted, and whether it resolved. */
interface FormulaValue {
  /** Formatted for display. {@link EMPTY} when the run resolved nothing. */
  text: string;
  /** False when the run resolved no value at all — distinct from a value of zero. */
  resolved: boolean;
}

/** One milestone's formula rewritten as `base + N months = result`, values substituted. */
export interface SubstitutedMilestoneFormula {
  /** The date the lag is applied to. */
  base: FormulaValue;
  /** The lag, in months. A real 0 is "0" and resolved; an unresolved lag is {@link EMPTY}. */
  lag: FormulaValue;
  /** The resulting milestone date, formatted like every other date on the tearsheet. */
  result: string;
}

/**
 * ── ANDREW'S ONE-LINE MILESTONE FORMULA, WITH VALUES SUBSTITUTED (UAT 2b, INS-12205) ─────
 *
 * UAT 2 printed the formula in variable names on one line and its operands' values on a
 * second. Andrew asked for a single line with the values in place — `09/01/2026 + 7 months
 * = 04/01/2027`. Every part is already in the payload, so NOTHING is computed here: the
 * base, the lag and the RESULT are each read, never added.
 *
 *   result  the milestone's own resolved date, `dates[milestone].value`.
 *   lag     the one months operand this line names, resolved through {@link scheduleOperands}
 *           — so a real 0 stays "0" and an unresolved lag stays {@link EMPTY}, never 0.
 *   base    the date the lag is applied to: the `reference_date` operand when the line names
 *           one, otherwise the PREVIOUS milestone's own date. A build-on-previous line
 *           ("first_production = completion + lag") lists only its lag, because the row above
 *           is already on screen (slot_valuation_inputs.rb#apply_milestone_formula), so its
 *           base is that row's value.
 *
 * ── null MEANS "NOT A `base + one month-lag` LINE" — KEEP THE OLD RENDERING ───────────────
 *
 * Classified by the SERVED unit, never by parsing the formula text or matching key names —
 * the declared-not-derived rule the rest of this file keeps. A line qualifies only when it
 * names exactly one months operand, at most one date operand, and nothing else. That leaves
 * the two irregular LTD lines out by construction: ltd spud (`reference + offset +
 * pace × rank` — two months operands and a rank) and ltd permit (`spud − lag` — a
 * subtraction whose base is the row BELOW, so no previous milestone supplies it). Both are
 * the "known adjacent issue" INS-12205 must not chase, so both keep their existing rendering.
 *
 * Until the model map lands every unit is undefined, so this returns null and the row shows
 * the variable-name formula it showed before — the same progressive enhancement the units,
 * labels and rate emphasis on this page already follow (contract note 1).
 *
 * ── AND null WHEN AN OPERAND DID NOT RESOLVE ─────────────────────────────────────────────
 *
 * The third null, and the reason is at the return below: the line's result comes from the
 * milestone itself rather than from the operands, so it can be present while they are not,
 * and `— + — months = 01/01/2027` is an equation the page cannot stand behind. Substitution
 * is therefore all-or-nothing. A resolved 0 substitutes; only an absent value withdraws.
 *
 * @param {MilestoneKey} milestone - Which milestone row this is.
 * @param {object} dates - The payload's `dates`, for a build-on-previous line's base.
 * @param {SlotInputNode[]} nodes - Every resolved slot input, to resolve operands against.
 * @param {object} meta - Served node definitions, for each operand's unit.
 * @return {(SubstitutedMilestoneFormula|null)} The one-line parts, or null to fall back.
 */
export function substitutedMilestoneFormula(
  milestone: MilestoneKey,
  dates: Partial<Record<MilestoneKey, TearsheetDate>>,
  nodes: SlotInputNode[],
  meta: Partial<Record<string, { label?: string; unit?: NodeUnit }>>
): SubstitutedMilestoneFormula | null {
  const merged = dates[milestone];
  const result = merged?.value;
  if (!merged || result === null || result === undefined) {
    return null;
  }

  const operands = scheduleOperands(merged.formulaInputs, nodes, meta);
  const months = operands.filter(
    (operand) => meta[operand.key]?.unit === 'months'
  );
  const bases = operands.filter(
    (operand) => meta[operand.key]?.unit === 'date'
  );
  // Exactly one lag, an optional single base date, and no third kind of operand. Anything
  // else is not a plain `base + N months` line — the irregular LTD rows fall through here.
  if (
    months.length !== 1 ||
    bases.length > 1 ||
    operands.length !== months.length + bases.length
  ) {
    return null;
  }

  const base: FormulaValue | null =
    bases.length === 1
      ? { text: bases[0].text, resolved: bases[0].resolved }
      : previousMilestoneDate(milestone, dates);
  if (!base) {
    return null;
  }

  const lag: FormulaValue = {
    text: months[0].text,
    resolved: months[0].resolved,
  };

  // ── SUBSTITUTION IS ALL-OR-NOTHING ─────────────────────────────────────────────────────
  //
  // The `result` above is NOT read from the operands — it is the milestone's own
  // `dates[milestone].value`, which resolves through the schedule merger whether or not the
  // scenario_config nodes this line names resolved. So the two halves of this line come from
  // INDEPENDENT sources and can disagree, and when they do the row asserted an arithmetic it
  // could not show:
  //
  //     — + — months = 01/01/2027
  //
  // Measured on a duc slot whose reference_date and duc_reference_to_completion_lag_months
  // carried no resolved value: the result was right and both operands were blank. A reader
  // cannot check that line, and a line that cannot be checked is worse than one that does not
  // claim to be checkable — the same rule as for a wrong-but-plausible
  // binding, one step further along.
  //
  // So an unresolved operand withdraws the whole one-line form and the row falls back to the
  // variable-name formula plus its operand list, which NAMES the missing operand and marks it
  // (the rendering every row had before the one-line form, and the one the two irregular LTD rows
  // still use). Nothing is invented and nothing is lost: the milestone's value keeps its own
  // column, so the date the reader came for is still on screen.
  //
  // A RESOLVED ZERO IS NOT UNRESOLVED and still substitutes — `resolved` keys off the entry's
  // existence, never the value's truthiness (see scheduleOperands). `duc_completion_to_
  // first_production_lag_months` is 0 on the measured DUC and renders `04/01/2026 + 0 months`.
  if (!base.resolved || !lag.resolved) {
    return null;
  }

  return {
    base,
    lag,
    result: formatNodeValue(result, 'date'),
  };
}

/**
 * The date of the milestone one lifecycle step earlier — the base a build-on-previous line
 * applies its lag to, since that line lists only the lag. Null when there is no earlier
 * milestone (permitDate) or it carries no value.
 *
 * @param {MilestoneKey} milestone - The milestone whose predecessor to read.
 * @param {object} dates - The payload's `dates`.
 * @return {(FormulaValue|null)} The predecessor's date, or null when there is none.
 */
function previousMilestoneDate(
  milestone: MilestoneKey,
  dates: Partial<Record<MilestoneKey, TearsheetDate>>
): FormulaValue | null {
  const index = MILESTONE_ORDER.indexOf(milestone);
  const previous = index > 0 ? dates[MILESTONE_ORDER[index - 1]] : undefined;
  if (!previous || previous.value === null || previous.value === undefined) {
    return null;
  }
  return { text: formatNodeValue(previous.value, 'date'), resolved: true };
}

// ── provenanceSummary AND provenanceTagType WERE HERE, AND ARE GONE (Jason, PR #7465) ────
//
// Neither ever rendered. provenanceTagType had exactly one reference in app/javascript —
// its own definition. provenanceSummary had four, all in this directory's tests. The
// INS-12204 work then ADDED a not_paceable branch and three examples to a helper nothing
// called, which is how a dead helper acquires the look of live code.
//
// The behaviour they appear to provide is real and lives elsewhere: the not-paced decision
// is made reader-side in NodeBinder (node_binder.rb:502-528, NOT_PACEABLE_REASON), and the
// copy a user actually sees comes from `absentProvenanceNote` below, which
// tearsheet-table-view.tsx:776 renders. Andrew asked for the wording to stop saying "No
// area matched" on a slot that was never paced; he did not ask for a tag, and no tag was
// ever on the page.
//
// Deleted rather than wired up: rendering a Carbon Tag nobody requested would be a real UI
// change dressed as a review fix.

/**
 * A caption for a domain whose scenario AND area are both absent, or null when there is
 * nothing to say.
 *
 * ── SPEC 5.4: AN ABSENT VALUE MUST NOT RENDER AS "NO SCENARIO WAS APPLIED" ────────────
 *
 * Today it renders as NOTHING — `ZoneSectionView` omits the whole context span when both
 * are blank — and nothing is the worst of the options, because it collapses two opposite
 * facts into the same silence:
 *
 *   status: 'not_applicable'   A CLAIM, and a true one. The domain has no scenario concept
 *                              (price_deck, royalty_revenue, expenses on every slot).
 *   status: null               UNKNOWN. No provenance record exists for this domain. On EC
 *                              61's DUC slot that is FIVE of the seven domains.
 *
 * With REQUIRE_PROVENANCE false the tearsheet opens on both, so the page has to say which
 * it is. This returns the sentence; the caller styles the two kinds differently.
 *
 * The unknown arm is then split again by `header.provenanceCapture`, which is the whole
 * point of that field: a run that PREDATES the recording is unrecoverable (scenarios are
 * unversioned), a capture that FAILED is recoverable by re-running, and a capture that
 * SUCCEEDED means this domain genuinely had nothing to record. Three different things to
 * tell a user, and before this they were one blank space.
 *
 * @param {DomainProvenance} [provenance] - The domain's provenance block, or null.
 * @param {ProvenanceCapture} [capture] - `header.provenanceCapture`, or null.
 * @return {object} `{ text, kind }` where kind is 'claim' or 'unknown', or null.
 */
export function absentProvenanceNote(
  provenance: DomainProvenance | null | undefined,
  capture: ProvenanceCapture | null | undefined
): { text: string; kind: 'claim' | 'unknown' } | null {
  switch (provenance?.status) {
    case 'not_applicable':
      if (provenance.reason === 'no_area_layer') {
        return { text: 'No area layer for this domain', kind: 'claim' };
      }
      if (provenance.reason === 'no_case') {
        return { text: 'Legacy run — no case to match against', kind: 'claim' };
      }
      // Worded against the `unmatched` arm below, and deliberately naming the CAUSE. An
      // earlier draft said "this slot is not scheduled by development pace", which reads as a
      // state that could change — as though the slot were merely awaiting a schedule. It is
      // the CATEGORY that is ineligible, not this slot's current condition, and saying so
      // also survives a new category being added to PACEABLE_CATEGORIES.
      if (provenance.reason === 'not_paceable') {
        return {
          text: "Development pace does not schedule this slot's category, so no area applies",
          kind: 'claim',
        };
      }
      // ── THE AREA WAS CAPTURED; THE VALUES WERE NOT APPLIED ───────────────────────────
      //
      // The sentence above is only half true once a capture exists: the pace still does not
      // schedule this category, but an area DOES apply and its lag values are on screen. This
      // is the qualification those rows carry — a per-row badge, a fade and a
      // row list were declined, so the block caption is what says the values were not used.
      //
      // The two branches are NOT interchangeable. A non-paceable slot whose run captured
      // nothing keeps the sentence above, where "no area applies" is still exactly right.
      if (provenance.reason === 'captured_not_scheduled') {
        // ── ⚠️ IT USED TO SAY "These values come from the area" ─────────────────────────
        //
        // True of the area's own rows and false of the rest of the block, which also holds
        // `first_production_date`, `cashflow_date` and the well's reported dates — values the
        // run did use. The caption could not distinguish them; the per-row marker can, so the
        // caption now points at the marks instead of speaking for every row beneath it.
        //
        // ⚠️ THE ROW MARKER DOES NOT REUSE THIS SENTENCE ANY MORE. It used to, which kept the
        // two in step for free, and it cannot now: a toggletip on a marked row that reads
        // "Rows marked 'not used'…" is talking about itself. See NOT_USED_CAPTURED_AREA.
        return {
          text:
            "Development pace does not schedule this slot's category. Rows marked " +
            '"not used" come from the area it falls in and were not applied.',
          kind: 'claim',
        };
      }
      return { text: 'No scenario applies to this domain', kind: 'claim' };
    case 'unmatched':
      return { text: 'A match was attempted; no area matched', kind: 'claim' };
    case 'matched':
    case 'global_default':
      // The caller only asks about a blank domain, so these should not arrive. Say nothing
      // rather than invent a caption for a state that HAS provenance.
      return null;
    default:
      break;
  }

  // status is null or the whole block is absent: UNKNOWN, not a claim.
  if (!capture) {
    return { text: 'Provenance not recorded for this run', kind: 'unknown' };
  }
  if (capture.status === 'failed') {
    return {
      text: 'Provenance could not be recorded for this run',
      kind: 'unknown',
    };
  }
  return { text: 'No scenario recorded for this domain', kind: 'unknown' };
}

/**
 * AC 13's named output rows, in the order the payload has always emitted them.
 *
 * ── WHY A DECLARED LIST AND NOT `Object.keys(outputs.values)` ─────────────────────────
 *
 * The rows used to be built from whatever keys the payload happened to carry, so a column
 * the calculator never wrote produced no key, no row, and NOTHING ON SCREEN SAYING
 * ANYTHING WAS MISSING. AC 13 names its rows explicitly, so they come from the criterion.
 *
 * THIS IS ABOUT TO MATTER MORE, NOT LESS. `irr` and `payout_month` are written by
 * eval_calculator PR #501, which is UNMERGED: those columns exist only in parquets written
 * by that branch. On this machine every slot that opens carries both keys (measured across
 * all 11 slots of EC 61 and all 4 of EC 59 — always present, always null), so the absent-key
 * arm is unreachable here. On sandbox or production the keys will simply not exist.
 *
 * The order matches what the endpoint emits today, so declaring the list moves no row.
 * Any key the payload carries that is NOT listed here still renders, appended — a new
 * calculator column appears without a frontend edit.
 */
// ── UAT 6: THE NPV PAIRS, GROUPED BY THE COMMISSION THAT DISTINGUISHES THEM ──────────────
//
// Andrew read the six NPV rows as three duplicated pairs. Often they ARE identical:
// `excluded = included × (1 − pct/100)`, verified to 12 decimal places, so a zero rate makes
// a pair genuinely equal. The rate is the only thing that tells them apart, and it was
// nowhere on the page.
//
// GROUPED, NOT COLLAPSED. The mtm pair will keep looking identical, permanently — mtm is zero
// on 1,649 of 1,650 scenarios — and that is a true fact about the configuration, not a
// rendering fault. Heading the group "MTM commission 0.0%" makes the sameness LEGIBLE: the
// reader sees why the two numbers agree instead of wondering whether the page repeated
// itself. Hiding the pair, or merging it into one row, would be hiding a real answer.

/** One commission group: which rate governs it, and the two rows it heads. */
export const COMMISSION_GROUPS = [
  {
    kind: 'target' as const,
    label: 'Target',
    keys: ['target_npv_excluded', 'target_npv_included'],
  },
  {
    kind: 'max' as const,
    label: 'Max',
    keys: ['max_npv_excluded', 'max_npv_included'],
  },
  {
    kind: 'mtm' as const,
    label: 'MTM',
    keys: ['mtm_npv_excluded', 'mtm_npv_included'],
  },
] as const;

export type CommissionKind = (typeof COMMISSION_GROUPS)[number]['kind'];

/**
 * A commission rate as it appears in a heading, or null when it could not be read.
 *
 * ── 0 RETURNS "0.0%" AND null RETURNS null ──────────────────────────────────────────────
 *
 * Not the same fact and not interchangeable. 0 is a rate the scenario really carries; null is
 * one we could not read, and printing "0.0%" for it would assert a configuration that may not
 * exist. The `?? null` guard is doing real work — `rate || null` would turn a genuine 0 into
 * "not recorded", which is the exact inversion this distinction exists to prevent.
 *
 * @param {(number|null)} [rate] - The percentage, where 5.2375 means 5.2375%.
 * @return {(string|null)} The formatted rate, or null when unknown.
 */
export function formatCommissionRate(
  rate: number | null | undefined
): string | null {
  if (typeof rate !== 'number' || Number.isNaN(rate)) {
    return null;
  }
  // `Number(x.toFixed(4))` is ROUNDING, not display — the form utils/number.ts's lint rule
  // explicitly allows — and String() then drops the trailing zeros a fixed-precision
  // formatter would leave on. One decimal is restored where rounding removed the point
  // entirely, so a configured zero reads "0.0%" rather than "0%": it has to look like a
  // rate someone set, not like an absence.
  const rounded = Number(rate.toFixed(4));
  const text = String(rounded);
  return `${text.includes('.') ? text : `${text}.0`}%`;
}

/**
 * The heading above one commission group.
 *
 * Names the rate where we have it and says so plainly where we do not. "rate not recorded" is
 * deliberately not "0.0%" — see formatCommissionRate.
 *
 * @param {string} label - The group's display name.
 * @param {(number|null)} [rate] - Its commission percentage.
 * @return {string} The heading.
 */
export function commissionGroupHeading(
  label: string,
  rate: number | null | undefined
): string {
  const formatted = formatCommissionRate(rate);
  return formatted === null
    ? `${label} commission — rate not recorded`
    : `${label} commission ${formatted}`;
}

/**
 * The caption under a ZERO-rate group, or null.
 *
 * States the consequence the reader is looking at, so an identical pair reads as arithmetic
 * rather than as a repeated row. Only at exactly 0 — at any other rate the two differ and
 * the caption would be false.
 *
 * @param {(number|null)} [rate] - The group's commission percentage.
 * @return {(string|null)} The caption, or null when it would not be true.
 */
export function commissionGroupCaption(
  rate: number | null | undefined
): string | null {
  return rate === 0
    ? 'At 0% the two rows are equal by definition, not repeated.'
    : null;
}

export const AC13_OUTPUT_KEYS = [
  'target_npv_excluded',
  'target_npv_included',
  'max_npv_excluded',
  'max_npv_included',
  'mtm_npv_excluded',
  'mtm_npv_included',
  'irr',
  'payout_month',
] as const;

/**
 * Units for the AC 13 output keys, used ONLY when the model map failed to load (`metaFailed`)
 * so `meta[key].unit` will never arrive.
 *
 * Without it, `irr` — a RAW xirr fraction (0.1852 for an 18.52% return) — falls to the
 * no-unit `format0` arm of {@link formatOutputValue} and renders as `0`: the one thing
 * {@link PendingValue}'s contract forbids, a confidently wrong number. During LOADING the row
 * still shows PendingValue (the real unit may yet arrive); this fallback applies only on a
 * TERMINAL failure, where a best-effort correct render beats a wrong one.
 *
 * The eight output keys are frozen registry constants (see {@link AC13_OUTPUT_KEYS} and
 * model_map.rb). Only `irr` needs scaling — the six NPVs are plain numbers and `payout_month`
 * is a date/count — so it is the single entry here. Add an entry if a scaled output is ever
 * introduced; keeping it minimal keeps this from drifting into a second copy of the registry.
 */
export const FALLBACK_OUTPUT_UNITS: Partial<Record<string, NodeUnit>> = {
  irr: 'fraction',
};

/**
 * The three states an AC 13 output row can be in.
 *
 * `no_value` and `not_computed` are DIFFERENT FACTS and both rendered as nothing before
 * this existed:
 *
 *   no_value      the key is there and null. The value does not exist FOR THIS WELL — no
 *                 IRR because the leasehold stream never changes sign, no payout because
 *                 `net_working_interest` is 0.0 so nothing was invested to recover.
 *   not_computed  the key is ABSENT. This calculation produced no such column at all —
 *                 the run predates PR #501. Nobody computed one; that is not the same as
 *                 the well not having one.
 */
export type OutputRowState = 'value' | 'no_value' | 'not_computed';

/**
 * Which of the three states a given output key is in.
 *
 * Uses `in` rather than a truthiness or undefined check, because ABSENT and NULL are the
 * distinction this whole function exists to preserve — `values[key] === undefined` would
 * collapse them again. `0` and `false` are real values and are reported as such.
 *
 * AN UNVALUED SLOT IS `no_value`, NOT `not_computed`. The backend sends `values: null` with
 * an `unvaluedReason` when it deliberately did not value the slot; the reason is stated once
 * above the rows, and "Not computed by this run" on every row beneath it would blame the run
 * for a decision about this slot.
 *
 * @param {object} [values] - `outputs.values`, which may itself be absent.
 * @param {string} key - The output key.
 * @param {boolean} [unvalued] - Whether the payload carries an `unvaluedReason`.
 * @return {OutputRowState} The row's state.
 */
export function outputRowState(
  values: TearsheetOutputs['values'] | null | undefined,
  key: string,
  unvalued = false
): OutputRowState {
  if (!values || !(key in values)) {
    return unvalued ? 'no_value' : 'not_computed';
  }
  return values[key] === null || values[key] === undefined
    ? 'no_value'
    : 'value';
}

/**
 * The output rows to render: AC 13's declared list, then any extra key the payload carries.
 *
 * @param {object} [values] - `outputs.values`.
 * @param {boolean} [unvalued] - Whether the payload carries an `unvaluedReason`.
 * @return {Array} `{ key, state }` in render order.
 */
export function outputRows(
  values: TearsheetOutputs['values'] | null | undefined,
  unvalued = false
): Array<{ key: string; state: OutputRowState }> {
  const declared = AC13_OUTPUT_KEYS.map((key) => ({
    key: key as string,
    state: outputRowState(values, key, unvalued),
  }));
  const extra = Object.keys(values ?? {})
    .filter((key) => !AC13_OUTPUT_KEYS.includes(key as never))
    .map((key) => ({ key, state: outputRowState(values, key, unvalued) }));
  return [...declared, ...extra];
}

/**
 * What the user should do about a permanent failure, keyed on the machine-readable
 * `remedy` the endpoint sends (ProvenancePolicy::REMEDY_RERUN, provenance_policy.rb:99).
 *
 * AC 3 asks the permanent state to say two things: that the evaluation predates the
 * recorded provenance, and that re-running it produces a complete record. The endpoint
 * supplies the first as `message` (slot_valuation_inputs_controller.rb:46, "This
 * evaluation predates the data the tearsheet needs.") and the second only as this
 * machine-readable key — the backend deliberately sends no copy for it
 * (provenance_policy.rb:92-95). So the second sentence is composed here.
 *
 * REGISTER: factual, present tense, no alarm. Nothing is broken — this evaluation ran
 * before the record existed, and re-running it fixes that. No "error", no "failed", no
 * exclamation, and no instruction to contact anyone.
 */
export const REMEDY_COPY: Record<TearsheetRemedy, string> = {
  rerun_evaluation:
    'Re-running this evaluation will record everything the tearsheet needs.',
};

/** Fallback when the endpoint sent no `message` and no recognised `remedy`. */
export const ERROR_FALLBACK_COPY =
  'The slot valuation inputs could not be loaded.';

/** A retryable failure invites a retry; the copy says so before the button does. */
export const RETRY_HINT_COPY = 'This may succeed on retry.';

/**
 * Composes the subtitle for the "Tearsheet unavailable" panel from the error envelope.
 *
 * The two states must read differently (AC 3): a transient failure is worth retrying and
 * says so, a permanent one explains what is absent and what to do instead. Pure and
 * exported so the wording is asserted directly rather than through a rendered panel.
 *
 * @param {object} [error] - The parsed envelope fields off `TearsheetApiError`.
 * @return {string} The subtitle to render.
 */
export function tearsheetErrorSubtitle(error?: {
  message?: string;
  retryable?: boolean;
  remedy?: TearsheetRemedy;
}): string {
  const message = error?.message?.trim();
  if (error?.retryable) {
    return [message, RETRY_HINT_COPY].filter(Boolean).join(' ');
  }
  const remedy = error?.remedy ? REMEDY_COPY[error.remedy] : undefined;
  return [message ?? ERROR_FALLBACK_COPY, remedy].filter(Boolean).join(' ');
}

/**
 * Summary of a monthly series' extent, derived once per payload from
 * `outputs.cashflowSeries.rows` — which carries every month's `date`, so nothing
 * new is served for this.
 */
export interface SeriesExtent {
  /** ISO date of the last month present. */
  lastDate: string | null;
  /** Calendar months absent between the first and last present month. */
  monthsAbsent: number;
}

/**
 * Measures a monthly series' extent and how much of it is missing.
 *
 * @param {Array} [rows] - `outputs.cashflowSeries.rows`, ascending by date.
 * @return {SeriesExtent} The last date, and the count of absent calendar months.
 */
export function seriesExtent(
  rows: Array<{ date: string }> | undefined | null
): SeriesExtent {
  if (!rows || rows.length === 0) {
    return { lastDate: null, monthsAbsent: 0 };
  }
  const first = new Date(`${rows[0].date}T00:00:00Z`);
  const last = new Date(`${rows[rows.length - 1].date}T00:00:00Z`);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) {
    return { lastDate: rows[rows.length - 1].date, monthsAbsent: 0 };
  }
  const span =
    (last.getUTCFullYear() - first.getUTCFullYear()) * 12 +
    (last.getUTCMonth() - first.getUTCMonth()) +
    1;
  return {
    lastDate: rows[rows.length - 1].date,
    monthsAbsent: Math.max(0, span - rows.length),
  };
}

/**
 * The caption under a monthly node's four-point preview (INS-11823 QA).
 *
 * ── WHY A CAPTION AND NOT MORE VALUES ────────────────────────────────────────────────
 *
 * The preview is `points.first(4)` (node_binder.rb:104) — the EARLIEST four, not a
 * sample and not first-plus-last. The badge said `monthly · 361`, which states the
 * extent but never that you are looking at the beginning of it, so four true numbers
 * read as though they were the whole story. Measured on EC 61's DUC slot: rows 1-4 of
 * 361, the last of them Jun 2026, with the series running to Mar 2056.
 *
 * ── NO SUM, DELIBERATELY ─────────────────────────────────────────────────────────────
 *
 * A total is meaningless for a rate or a price, and for a volume it is a figure the
 * calculator never emitted — on a page whose whole claim is "this is what produced the
 * number", inventing one contradicts the premise. There is also a concrete collision:
 * `outputs.values` already carries the calculator's own aggregates, so a
 * page-computed total could sit beside an authoritative one and disagree, which is the
 * double-rendered-NPV defect (deviations item 59) all over again.
 *
 * ── WHY "through" AND NOT A RANGE ────────────────────────────────────────────────────
 *
 * A range ("Apr 2025 – Mar 2056") implies every month between is present, and on most
 * slots that is false: measured across the five reference slots, THREE have a gap — 2
 * absent months on EC 59's PDP, 11 on EC 61's DUC, 4 on EC 64's PDP — always exactly
 * one gap, immediately after the first row. So the extent is stated as an endpoint, and
 * the absence is stated separately and only when there is one. The start date is
 * already visible: it is the first entry of the preview itself.
 *
 * @param {number} shown - How many points the preview renders.
 * @param {number} [seriesLength] - The node's served `seriesLength`.
 * @param {SeriesExtent} [extent] - From {@link seriesExtent}.
 * @return {(string|null)} The caption, or null when there is nothing to qualify.
 */
export function seriesPreviewCaption(
  shown: number,
  seriesLength: number | undefined,
  extent: SeriesExtent | undefined
): string | null {
  if (!seriesLength || shown === 0 || shown >= seriesLength) {
    return null;
  }
  const parts = [`first ${shown} of ${seriesLength} months`];
  const lastLabel = extent?.lastDate ? monthLabel(extent.lastDate) : null;
  if (lastLabel) {
    parts.push(`through ${lastLabel}`);
  }
  if (extent && extent.monthsAbsent > 0) {
    parts.push(
      `${extent.monthsAbsent} month${
        extent.monthsAbsent === 1 ? '' : 's'
      } absent`
    );
  }
  return parts.join(' · ');
}

/**
 * "Mar 2056" from "2056-03-01". Mirrors the backend's own month label
 * (node_binder.rb:318) so a month reads identically wherever it appears.
 *
 * @param {string} iso - An ISO date.
 * @return {(string|null)} The label, or null when unparseable.
 */
export function monthLabel(iso: string): string | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return d.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// ─────────────────────────────────────────────────────────────────────────────────────────
// WHICH OPERATION SET A VALUE
// ─────────────────────────────────────────────────────────────────────────────────────────
//
// ⚠️ WHAT THE LOG CANNOT TELL US, AND THEREFORE WHAT THE PAGE MUST NOT SAY.
//
// A stored override entry's keys are exactly `type`, `slot_id`/`slot_ids` and `overrides` —
// measured on EC 65's real committed operations. There is NO id, NO timestamp and NO author.
// So the page can say an edit was a bulk one; it cannot say WHICH bulk edit, when it
// happened, or who made it, and any wording implying otherwise is a claim nothing supports.
//
// IT ALSO CANNOT SAY "APPLIED TO SEVERAL SLOTS". EC 65's entries [0] and [1] are
// `type: "bulk"` carrying exactly ONE slot id, so `kind: 'bulk'` does not imply a multi-slot
// edit — it records which WRITE PATH produced the entry, nothing about its reach. The slot
// count is not in the payload either.
//
// WHAT IT CAN SAY, and the more useful reading of "identifies the operation": two fields
// reporting the same `kind` AND the same `index` were set by the SAME entry. On EC 65,
// spudDate and permitDate both report `{individual, 2}` — one edit, two fields — and that is
// checkable by the reader against the rows in front of them.

/**
 * A stable-within-this-response key for one override entry.
 *
 * Kind AND index, because index alone is ambiguous: the log is a single array, but a reader
 * comparing two values wants "same entry", and an entry is identified by its position in that
 * array. Kind is included so a future log that interleaves differently cannot collide.
 *
 * NOT FOR PERSISTENCE, a React key across fetches, or display. The index is positional and
 * persist-time compaction can shift it; it addresses an entry within THIS payload only.
 *
 * @param {OverrideWinner} winner - The `overriddenBy` a ResolvedValue carried.
 * @return {string} The grouping key.
 */
export function overrideOperationKey(winner: OverrideWinner): string {
  return `${winner.kind}:${winner.index}`;
}

/** One field an operation set, as the sibling list needs to name it. */
export interface OperationField {
  key: NodeKey;
  label: string;
}

/**
 * Groups this slot's overridden values by the operation that set them.
 *
 * Built once per payload rather than per row: a row asking "what else did my operation set"
 * would otherwise rescan every node, and the answer is the same for every member of a group.
 *
 * Only nodes carrying a `overriddenBy` participate. A node whose payload predates that key
 * contributes nothing and is not grouped with anything — silence, not a group of one.
 *
 * @param {SlotInputNode[]} nodes - The payload's `slotInputs`.
 * @return {Map<string, OperationField[]>} Operation key → the fields it set, in payload order.
 */
export function buildOverrideOperations(
  nodes: SlotInputNode[]
): Map<string, OperationField[]> {
  const groups = new Map<string, OperationField[]>();
  for (const node of nodes) {
    const winner = node.values.find(
      (value) => value.overridden === true && value.overriddenBy
    )?.overriddenBy;
    if (!winner) {
      continue;
    }
    const groupKey = overrideOperationKey(winner);
    const members = groups.get(groupKey) ?? [];
    members.push({ key: node.key, label: node.label });
    groups.set(groupKey, members);
  }
  return groups;
}

/**
 * The other fields the same operation set on this slot, named for display.
 *
 * Up to three then "and N more", matching the Calculate gate's tract list — one house
 * convention for "a list that could be long", not a second.
 *
 * @param {OperationField[]} [members] - The operation's fields, including this one.
 * @param {NodeKey} selfKey - The node asking, excluded from its own sibling list.
 * @return {string} The joined names, or '' when this operation set nothing else.
 */
export function siblingFieldList(
  members: OperationField[] | undefined,
  selfKey: NodeKey
): string {
  const names = (members ?? [])
    .filter((member) => member.key !== selfKey)
    .map((member) => member.label);
  if (names.length === 0) {
    return '';
  }
  const shown = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${shown} and ${names.length - 3} more` : shown;
}
